import type {
  ApiResponse,
  PersonSportRating,
  PersonSportRatingHistoryEntry,
  PersonSportRatingsBulkUpsertInput,
  PlayerRatingSettings,
  PlayerRatingSettingsInput,
} from '@court-sync/shared';
import { apiGet, apiPatch, apiPut } from './apiClient';

export const playerRatingService = {
  async getSettings(organizationId: string): Promise<ApiResponse<PlayerRatingSettings>> {
    return apiGet<PlayerRatingSettings>(`/organizations/${organizationId}/player-rating-settings`);
  },

  async updateSettings(
    organizationId: string,
    data: PlayerRatingSettingsInput
  ): Promise<ApiResponse<PlayerRatingSettings>> {
    return apiPatch<PlayerRatingSettings>(`/organizations/${organizationId}/player-rating-settings`, data);
  },

  async getClubPlayerRatingSettings(clubId: string): Promise<ApiResponse<PlayerRatingSettings>> {
    return apiGet<PlayerRatingSettings>(`/members/${clubId}/player-rating-settings`);
  },

  async getAcademyPlayerRatingSettings(academyId: string): Promise<ApiResponse<PlayerRatingSettings>> {
    return apiGet<PlayerRatingSettings>(`/academies/${academyId}/player-rating-settings`);
  },

  async getMemberSportRatings(clubId: string, memberId: string): Promise<ApiResponse<PersonSportRating[]>> {
    return apiGet<PersonSportRating[]>(`/members/${clubId}/members/${memberId}/sport-ratings`);
  },

  async updateMemberSportRatings(
    clubId: string,
    memberId: string,
    data: PersonSportRatingsBulkUpsertInput
  ): Promise<ApiResponse<PersonSportRating[]>> {
    return apiPut<PersonSportRating[]>(`/members/${clubId}/members/${memberId}/sport-ratings`, data);
  },

  async getMemberRatingHistory(
    clubId: string,
    memberId: string,
    params?: { organizationSportId?: string; limit?: number; offset?: number }
  ): Promise<PersonSportRatingHistoryEntry[]> {
    const search = new URLSearchParams();
    if (params?.organizationSportId) search.set('organizationSportId', params.organizationSportId);
    if (params?.limit != null) search.set('limit', String(params.limit));
    if (params?.offset != null) search.set('offset', String(params.offset));
    const qs = search.toString();
    const res = await apiGet<PersonSportRatingHistoryEntry[]>(
      `/members/${clubId}/members/${memberId}/sport-ratings/history${qs ? `?${qs}` : ''}`
    );
    return res.success && res.data ? res.data : [];
  },

  async getStudentSportRatings(
    academyId: string,
    studentId: string
  ): Promise<ApiResponse<PersonSportRating[]>> {
    return apiGet<PersonSportRating[]>(`/academies/${academyId}/students/${studentId}/sport-ratings`);
  },

  async updateStudentSportRatings(
    academyId: string,
    studentId: string,
    data: PersonSportRatingsBulkUpsertInput
  ): Promise<ApiResponse<PersonSportRating[]>> {
    return apiPut<PersonSportRating[]>(`/academies/${academyId}/students/${studentId}/sport-ratings`, data);
  },

  async getStudentRatingHistory(
    academyId: string,
    studentId: string,
    params?: { organizationSportId?: string; limit?: number; offset?: number }
  ): Promise<PersonSportRatingHistoryEntry[]> {
    const search = new URLSearchParams();
    if (params?.organizationSportId) search.set('organizationSportId', params.organizationSportId);
    if (params?.limit != null) search.set('limit', String(params.limit));
    if (params?.offset != null) search.set('offset', String(params.offset));
    const qs = search.toString();
    const res = await apiGet<PersonSportRatingHistoryEntry[]>(
      `/academies/${academyId}/students/${studentId}/sport-ratings/history${qs ? `?${qs}` : ''}`
    );
    return res.success && res.data ? res.data : [];
  },
};
